import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Star, TrendingUp, TrendingDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";
import { GradeBadge, ActionBadge, EarningsDot } from "../components/Badges";
import { num } from "../lib/format";

export default function Watchlist() {
  const nav = useNavigate();
  const [direction, setDirection] = useState("long");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/watchlist", { params: { direction } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [direction]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    await client.delete(`/watchlist/${t}`);
    toast(`Removed ${t}`);
    load();
  };

  return (
    <div className="px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-2xl font-black tracking-tight" data-testid="watchlist-title">WATCHLIST</h1>
          <p className="mt-1 text-sm text-gray-500">Promoted symbols · monitored for trigger &amp; confirmation.</p>
        </div>
        <div className="flex overflow-hidden rounded-sm border border-white/10">
          <button data-testid="wl-long" onClick={() => setDirection("long")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "long" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingUp size={13} />LONG</button>
          <button data-testid="wl-short" onClick={() => setDirection("short")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "short" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingDown size={13} />SHORT</button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-56 items-center justify-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
      ) : items.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center gap-2 border border-white/10 py-24 text-center">
          <Star size={26} className="text-gray-700" />
          <p className="text-sm text-gray-500">No symbols yet. Open any stock and tap <span className="text-white">Watch</span>.</p>
          <button onClick={() => nav("/")} className="mt-2 rounded-sm border border-white/10 px-4 py-2 font-mono-sg text-xs text-gray-300 hover:border-white/20">Go to Scanner</button>
        </div>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((r) => (
            <div key={r.ticker} data-testid="watchlist-card"
              onClick={() => nav(`/stock/${r.ticker}?direction=${direction}`)}
              className="cursor-pointer border border-white/10 bg-[#111111] p-4 transition-colors duration-200 hover:border-white/20">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-head text-xl font-black">{r.ticker}</div>
                  <div className="max-w-[150px] truncate text-xs text-gray-500">{r.name}</div>
                </div>
                <GradeBadge grade={r.grade} gradeKey={r.grade_key} score={r.score} />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <ActionBadge action={r.action} />
                <div className="flex items-center gap-2">
                  {r.live && (
                    <span data-testid="wl-live" className="inline-flex items-center gap-1 font-mono-sg text-xs font-bold text-[#00FF66]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66] sg-blink" /> ${num(r.live.price)}
                    </span>
                  )}
                  <EarningsDot level={r.earnings_level} />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 font-mono-sg text-xs text-gray-400">
                <div>RS <span className="text-white">{r.rs.rating}</span></div>
                <div>RVOL <span className="text-white">{num(r.live?.rvol ?? r.rvol)}</span></div>
                <div>R:R <span className="text-white">{num(r.trade.rr)}</span></div>
              </div>
              <button data-testid="wl-remove" onClick={(e) => { e.stopPropagation(); remove(r.ticker); }}
                className="mt-3 flex items-center gap-1 text-xs text-gray-600 transition-colors duration-200 hover:text-[#FF3B30]">
                <Trash2 size={12} /> remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
